import { SAUNA_SETUP_NOTE_MAX_LENGTH, type SaunaSessionSummary, type SaunaSetupNoteView } from '@jums-dashboard/shared';

export type SessionFilter = 'all' | 'completed';

export interface HeatingRatePoint {
  x: number;
  y: number;
  sessionId: string;
  lowConfidence: boolean;
}

export interface SetupNoteMarker {
  x: number;
  noteId: string;
  text: string;
}

export function filterSessions(sessions: readonly SaunaSessionSummary[], filter: SessionFilter): SaunaSessionSummary[] {
  return filter === 'completed' ? sessions.filter((s) => s.completed) : [...sessions];
}

/** One point per completed session with a computable heating rate, oldest first. */
export function buildHeatingRatePoints(sessions: readonly SaunaSessionSummary[]): HeatingRatePoint[] {
  const points: HeatingRatePoint[] = [];
  for (const s of sessions) {
    if (!s.completed || s.heatingRateCPerMin == null || !Number.isFinite(s.heatingRateCPerMin)) continue;
    const x = Date.parse(s.startedAt);
    if (!Number.isFinite(x)) continue;
    points.push({ x, y: s.heatingRateCPerMin, sessionId: s.sessionId, lowConfidence: s.heatingRateLowConfidence });
  }
  return points.sort((a, b) => a.x - b.x);
}

/** Setup-note markers that fall within `[minX, maxX]`, oldest first. */
export function buildSetupNoteMarkers(notes: readonly SaunaSetupNoteView[], minX: number, maxX: number): SetupNoteMarker[] {
  const markers: SetupNoteMarker[] = [];
  for (const n of notes) {
    const x = Date.parse(n.notedAt);
    if (!Number.isFinite(x) || x < minX || x > maxX) continue;
    markers.push({ x, noteId: n.noteId, text: n.text });
  }
  return markers.sort((a, b) => a.x - b.x);
}

export function formatTempC(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)} °C`;
}

export function formatHeatingTime(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '—';
  const total = Math.round(minutes);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export function formatHeatingRate(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  return `${rate.toFixed(2)} °C/min`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatShortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Formats a date as a `<input type="datetime-local">` value in local time. */
export function toDateTimeLocalValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Parses a `datetime-local` value (local time) to an ISO string, or null when invalid. */
export function fromDateTimeLocalValue(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  const date = new Date(y, mo - 1, d, h, mi);
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
  return date.toISOString();
}

/** Returns a validation message for setup-note text, or null when valid. */
export function validateSetupNoteText(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return 'Enter a note.';
  if (trimmed.length > SAUNA_SETUP_NOTE_MAX_LENGTH) return `Keep the note under ${SAUNA_SETUP_NOTE_MAX_LENGTH} characters.`;
  return null;
}
